import React from 'react';
import { makeStyles } from '@mui/styles';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getRiskDetail } from 'apis/riskAPIs';
import CircularProgress from '@mui/material/CircularProgress';
import { 
  Alert, 
  Card, 
  CardContent, 
  CardHeader, 
  Grid, 
  Typography, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  backButton: {
    marginBottom: theme.spacing(2)
  },
  card: {
    marginBottom: theme.spacing(2)
  },
  detailRow: {
    marginBottom: theme.spacing(1)
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  value: {
    color: theme.palette.text.primary
  },
  orderSection: {
    marginTop: theme.spacing(3)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600
  },
  itemImage: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius
  },
  totalRow: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[50]
  },
  grandTotalRow: {
    fontWeight: 700,
    backgroundColor: theme.palette.primary.light,
    '& td': {
      color: theme.palette.primary.contrastText,
      fontSize: '1.1rem'
    }
  }
}));

const RiskDetail = () => {
  const classes = useStyles();
  const { riskId } = useParams();
  const history = useHistory();
  const location = useLocation();
  
  const user = useSelector((state) => state.account.user);
  const currencySymbol = (user && user.currencySymbol) ? user.currencySymbol : "$";

  // Extract year from query parameters
  const searchParams = new URLSearchParams(location.search);
  const year = searchParams.get('year') || new Date().getFullYear();

  // React Query hook for fetching risk detail
  const {
    data: risk,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['risk', riskId, year],
    queryFn: () => getRiskDetail(riskId, year),
    retry: 2,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      console.log('Error fetching risk detail:', error);
      if (error.response && error.response.status === 403) {
        history.push('/sign-in');
      }
    }
  });
  // console.log('risk', risk);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD/MM/YYYY');
  };

  const formatDateTime = (timeString) => {
    if (!timeString) return 'N/A';
    return moment(timeString).format('DD/MM/YYYY HH:mm:ss');
  };

  const handleBack = () => {
    history.push('/risks');
  };

  if (isLoading) {
    return (
      <div className={classes.root}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <CircularProgress size={150} />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={classes.root}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          className={classes.backButton}
        >
          Back to Risks
        </Button>
        <Alert severity="error">
          {error?.response?.data?.message || 'Error in retrieving risk details'}
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        className={classes.backButton}
        variant="outlined"
      >
        Back to Risks
      </Button>

      <Card className={classes.card}>
        <CardHeader
          title={`Risk Detail - Order #${risk?.orderNumber || 'N/A'}`}
          titleTypographyProps={{ variant: 'h5' }}
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Order Number
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk?.orderNumber || 'N/A'}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Risk Type
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk?.riskType || 'N/A'}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Order Year
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk?.orderYearString || 'N/A'}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Order Date
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {formatDate(risk?.orderDateString)}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Order Time
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {formatDateTime(risk?.orderTime)}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Restaurant
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk?.restaurantName || 'N/A'}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Restaurant ID
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk?.restaurantId || 'N/A'}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Amount
                </Typography>
                <Typography variant="h6" className={classes.value} color="primary">
                  {currencySymbol}{risk?.amount ? Number(risk.amount).toFixed(2) : '0.00'}
                </Typography>
              </div>
            </Grid>
          </Grid>

        </CardContent>
      </Card>

      {risk?.order && (
        <Card className={classes.card}>
          <CardHeader
            title="Order Information"
            titleTypographyProps={{ variant: 'h5' }}
          />
          <CardContent>
            {/* Order Info */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" className={classes.label}>
                  Order Number
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk.order.orderNumber || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" className={classes.label}>
                  Order Type
                </Typography>
                <Chip 
                  label={risk.order.type || 'N/A'} 
                  color={risk.order.type === 'pickup' ? 'primary' : 'secondary'}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" className={classes.label}>
                  Order Time
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk.order.orderTime || 'N/A'}
                </Typography>
              </Grid>

              {risk.order.realTableId && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" className={classes.label}>
                    Table Number
                  </Typography>
                  <Typography variant="body1" className={classes.value}>
                    {risk.order.realTableId}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <Typography variant="body2" className={classes.label}>
                  Payment Type
                </Typography>
                <Typography variant="body1" className={classes.value}>
                  {risk.order.paymentType || 'N/A'}
                </Typography>
              </Grid>

              {risk.order.pickupTime && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" className={classes.label}>
                    Pickup Time
                  </Typography>
                  <Typography variant="body1" className={classes.value}>
                    {risk.order.pickupTime} ({risk.order.pickupTimeType || 'scheduled'})
                  </Typography>
                </Grid>
              )}

              {risk.order.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" className={classes.label}>
                    Order Notes
                  </Typography>
                  <Typography variant="body1" className={classes.value}>
                    {risk.order.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider style={{ margin: '24px 0' }} />

            {/* Items Section */}
            <Typography variant="h6" className={classes.sectionTitle}>
              Order Items
            </Typography>
            
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>Item</TableCell>
                    <TableCell className={classes.tableHeader} align="center">Quantity</TableCell>
                    <TableCell className={classes.tableHeader} align="right">Price</TableCell>
                    <TableCell className={classes.tableHeader} align="right">Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {risk.order.items && risk.order.items.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {item.dish?.imageUrl && (
                            <img 
                              src={item.dish.thumbImageUrl || item.dish.imageUrl} 
                              alt={item.dish.name}
                              className={classes.itemImage}
                            />
                          )}
                          <div>
                            <Typography variant="body1" style={{ fontWeight: 500 }}>
                              {item.dish?.name || 'Unknown Item'}
                            </Typography>
                            {item.dish?.description && (
                              <Typography variant="body2" color="textSecondary">
                                {item.dish.description}
                              </Typography>
                            )}
                            {item.specialInstructions && (
                              <Typography variant="body2" color="primary" style={{ fontStyle: 'italic', marginTop: '4px' }}>
                                Note: {item.specialInstructions}
                              </Typography>
                            )}
                            {item.options && item.options.length > 0 && (
                              <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
                                Options: {item.options.map(opt => opt.name).join(', ')}
                              </Typography>
                            )}
                            {item.size && (
                              <Typography variant="body2" color="textSecondary">
                                Size: {item.size.name}
                              </Typography>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1">{item.quantity}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          {currencySymbol}{(item.dish?.price || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                          {currencySymbol}{(item.subTotal || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Subtotal */}
                  <TableRow className={classes.totalRow}>
                    <TableCell colSpan={3} align="right">
                      <Typography variant="body1">Subtotal:</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1">
                        {currencySymbol}{(risk.order.total || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Surcharges */}
                  {risk.order.surcharges && risk.order.surcharges.map((surcharge, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={3} align="right">
                        <Typography variant="body2" color="textSecondary">
                          {surcharge.name}:
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="textSecondary">
                          {currencySymbol}{(surcharge.amount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Grand Total */}
                  <TableRow className={classes.grandTotalRow}>
                    <TableCell colSpan={3} align="right">
                      <Typography variant="h6">Total:</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6">
                        {currencySymbol}{(risk.order.orderTotal || risk.order.total || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RiskDetail;
